import {http} from '../../http';
import sortBy from "lodash/sortBy";
import Apps from '../../../core/Apps';
import AccessLevel from '../../../core/AccessLevel';
import SubuserType from "@/core/SubuserType";

export default {
    state: {
        practitioners: [],
        loaded: false,
        loading: false
    },

    getters: {
        getPractitioners: (state, getters) => {
            return sortBy(state.practitioners, 'realname');
        },

        hasPractitioners: (state, getters) => {
            return getters.getPractitioners.length > 0;
        },

        getTenants: (state) => {
            const tenants = state.practitioners.filter(practitioner => practitioner.type === SubuserType.Tenant);

            return sortBy(tenants, 'realname');
        },

        hasTenants: (state, getters) => {
            return getters.getTenants.length > 0;
        },

        getEmployees: (state) => {
            const employees = state.practitioners.filter(practitioner => practitioner.type === SubuserType.Employee);

            return sortBy(employees, 'realname');
        },

        hasEmployees: (state, getters) => {
            return getters.getEmployees.length > 0;
        },

        find: (state, getters) => (user) => {
            const userId = typeof user === 'object' && user !== null ? user.id : parseInt(user) || 0;

            return getters.getPractitioners.find(p => p.id === userId);
        },

        isLoaded(state, getters) {
            return state.loaded;
        },

        getCalendarPermission: (state, getters) => (user, calendar) => {
            const calendarId = typeof calendar === 'object' && calendar !== null ? calendar.id : parseInt(calendar) || 0;
            const userId = typeof user === 'object' && user !== null ? user.id : parseInt(user) || 0;
            const practitioner = getters.getPractitioners.find(p => p.id === userId);

            if (!practitioner) {
                return null;
            }

            return (practitioner.calendar_permissions || []).find(cp => cp.calendar_id === calendarId);
        },

        canUserSeeCalendar: (state, getters) => (user, calendar) => {
            const userId = typeof user === 'object' && user !== null ? user.id : parseInt(user) || 0;
            const practitioner = getters.getPractitioners.find(p => p.id === userId);

            if (!practitioner) {
                return false;
            }

            if (practitioner.uses_new_general_permissions && !practitioner.uses_individual_calendar_permissions) {
                return practitioner.can_see_all_calendars;
            }

            const calendarPermission = getters.getCalendarPermission(user, calendar);
            return calendarPermission && calendarPermission.level.value >= AccessLevel.Show;
        },

        canUserEditCalendar: (state, getters) => (user, calendar) => {
            const userId = typeof user === 'object' && user !== null ? user.id : parseInt(user) || 0;
            const practitioner = getters.getPractitioners.find(p => p.id === userId);

            if (!practitioner) {
                return false;
            }

            if (practitioner.uses_new_general_permissions && !practitioner.uses_individual_calendar_permissions) {
                return practitioner.can_edit_all_calendars
            }

            const calendarPermission = getters.getCalendarPermission(user, calendar);
            return calendarPermission && calendarPermission.level.value >= AccessLevel.Edit;
        },

        label: (state, getters) => {
            if (getters.hasEmployees && getters.hasTenants) {
                return trans('usermanagement.employee_tenant');
            } else if (getters.hasTenants) {
                return trans('usermanagement.tenant');
            }

            return trans('usermanagement.employee');
        },

        labelSmall: (state, getters) => {
            return getters.label.toLowerCase();
        },

        labelPlural: (state, getters) => {
            if (getters.hasEmployees && getters.hasTenants) {
                return trans('usermanagement.employees_tenants');
            } else if (getters.hasTenants) {
                return trans('usermanagement.tenants');
            }

            return trans('usermanagement.employees');
        },

        labelPluralSmall: (state, getters) => {
            return getters.labelPlural.toLowerCase();
        },
    },

    mutations: {
        setPractitioners(state, practitioners) {
            state.practitioners = practitioners;
        },
    },

    actions: {
        loadPractitioners ({ commit, state, rootGetters }) {

            if (state.loaded || state.loading) {
                return;
            }

            const employeesAppEnabled = rootGetters['apps/isAppEnabled'](Apps.UserManagement);
            const tenantsAppEnabled = rootGetters['apps/isAppEnabled'](Apps.Tenants);

            if (! (employeesAppEnabled || tenantsAppEnabled)) {
                state.loaded = true;
                state.loading = false;
                return;
            }

            state.loading = true;

            http.get('/app/practitioners')
                .then(({ data }) => {
                    commit('setPractitioners', data);
                    state.loaded = true;
                    state.loading = false;
                }).catch(() => {
                    state.loading = false;
                });
        },

        reloadPractitioners({ state, dispatch }) {
            state.loaded = false;
            dispatch('loadPractitioners');
        },
    }
};
